import { useRef, useEffect, useCallback } from "react";

export function useAutoScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollTickRef = useRef(false);
  const userScrolledAwayRef = useRef(false);

  const isNearBottom = useCallback((): boolean => {
    const el = scrollRef.current;
    if (!el) return true;
    const viewport = el.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null;
    const target = viewport || el;
    return target.scrollHeight - target.scrollTop - target.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback((instant?: boolean, force?: boolean) => {
    if (!force && userScrolledAwayRef.current) return;
    if (scrollTickRef.current && !instant) return;
    scrollTickRef.current = true;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: instant ? "instant" : "smooth", block: "end" });
      setTimeout(() => { scrollTickRef.current = false; }, 150);
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const viewport = el.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null;
    const target = viewport || el;

    const handleScroll = () => {
      userScrolledAwayRef.current = !isNearBottom();
    };

    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  });

  const resetScrollFlag = useCallback(() => {
    userScrolledAwayRef.current = false;
  }, []);

  return {
    scrollRef,
    messagesEndRef,
    userScrolledAwayRef,
    scrollToBottom,
    resetScrollFlag,
  };
}
